import type { Option, ScenarioOptionGroup } from '@src/components/combobox/ComboboxBehavior';

/** The shape `fetchAllMessages` returns - one entry per question in the project. */
export interface ScenarioMessage {
  message: string;
  scenarioId: string;
}

/**
 * LF360-1938: turn the flat `fetchAllMessages` payload into one group per
 * scenario for the condition picker's sidebar.
 *
 * Before this, `BehaviorRulesNodeInfo` discarded the `scenarioId` the payload
 * had always carried and poured every other scenario's questions into a single
 * "Other scenarios" bucket - so two scenarios owning the same question text
 * were indistinguishable.
 *
 * Kept here rather than inside the component so the grouping rules are testable
 * without mounting the node-info panel.
 *
 * @param allMessages every question in the project, each tagged with its scenarioId.
 * @param currentScenarioMessages questions already offered under "This scenario";
 *        excluded here so the same question is not selectable twice.
 * @param scenarioNames scenarioId -> display name, from `fetchAllScenario`.
 */
export const buildOtherScenarioGroups = (
  allMessages: ScenarioMessage[],
  currentScenarioMessages: string[],
  scenarioNames: Map<string, string>,
): ScenarioOptionGroup[] => {
  const currentMessages = new Set(currentScenarioMessages);
  const byScenario = new Map<string, Option[]>();

  allMessages.forEach(({ message, scenarioId }) => {
    if (currentMessages.has(message)) return;
    const items = byScenario.get(scenarioId) ?? [];
    // The same question text can legitimately repeat inside one scenario;
    // offering it twice in the picker would just be noise.
    if (items.some((item) => item.value === message)) return;
    items.push({ label: message, value: message });
    byScenario.set(scenarioId, items);
  });

  return [...byScenario.entries()].map(([scenarioId, items]) => ({
    scenarioId,
    // Falling back to the raw id rather than dropping the group - a scenario
    // whose name has not loaded yet must still expose its questions, or they
    // become unreachable in the picker.
    scenarioName: scenarioNames.get(scenarioId) ?? scenarioId,
    items,
  }));
};
